import { type ApolloCache, type Reference, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";

import { DELETE_SERC_AGENCY, GET_SERC_AGENCY } from "./schema";

/**
 * Removes a deleted SERC agency from the cache (list + evict entity).
 * Use from mutation update like: update(cache, { data }) { if (data?.deleteSercAgency) removeSercAgencyFromCache(cache, data.deleteSercAgency); }
 */
function removeSercAgencyFromCache(cache: ApolloCache<unknown>, deleted: { id: string }) {
  const ref =
    cache.identify({ __typename: "SercAgency", id: deleted.id }) ?? cache.identify(deleted);
  if (!ref) return;
  cache.modify({
    fields: {
      sercAgencies(existing) {
        if (existing == null || typeof existing !== "object" || "__ref" in existing) {
          return existing;
        }
        const list = existing as {
          items: Reference[];
          count?: number;
        };
        if (!list.items) return existing;
        const newItems = list.items.filter((item: Reference) => item.__ref !== ref);
        if (newItems.length === list.items.length) return existing;
        return {
          ...list,
          items: newItems,
          count: Math.max(0, (list.count ?? 0) - 1),
        };
      },
    },
  });
  cache.evict({ id: ref });
  cache.gc();
}

type ConfirmDeleteSercAgencyProps = {
  sercAgencyId: string;
  onClose: () => void;
};

export function ConfirmDeleteSercAgency({ sercAgencyId, onClose }: ConfirmDeleteSercAgencyProps) {
  const alerts = useAlerts();

  const { data, previousData } = useQuery(GET_SERC_AGENCY, {
    fetchPolicy: "cache-and-network",
    variables: { id: sercAgencyId },
  });

  const [deleteSercAgency, { loading: isDeleting }] = useMutation(DELETE_SERC_AGENCY);

  const serc = data?.sercAgency ?? previousData?.sercAgency;

  if (!serc) {
    return null;
  }

  return (
    <ConfirmDialog
      open
      header="Delete SERC agency?"
      resources={[
        {
          resourceType: "SERC",
          resourceName: `${serc.state}${serc.email ? ` (${serc.email})` : ""}`,
        },
      ]}
      loading={isDeleting}
      onClose={onClose}
      onConfirm={() =>
        deleteSercAgency({
          variables: { id: sercAgencyId },
          onCompleted: () => {
            onClose();
            alerts.success("SERC agency deleted successfully");
          },
          onError: (e) => {
            alerts.error("Failed to delete SERC agency", e);
          },
          update(cache, { data }) {
            if (!data?.deleteSercAgency) return;
            removeSercAgencyFromCache(cache, data.deleteSercAgency);
          },
        })
      }
    />
  );
}
